import { Calendar, ChevronLeft, ChevronRight, DoorOpen, Pin, Plus, Sun } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@bookdental/ui";
import type { ViewMode } from "@/lib/agenda-navigation";
import { useFeature } from "@/lib/use-feature";
import { AgendaMiniCalendar } from "./AgendaMiniCalendar";
import { DentistDropdownButton } from "./DentistSelector";
import { ServiceDropdownButton } from "./ServiceSelector";

type DentistFilter = {
  ids: string[];
  toggle: (id: string) => void;
  selectAll: (allIds: string[]) => void;
  clear: () => void;
};

type ServiceFilter = DentistFilter;

type Props = {
  anchorDate: Date;
  viewMode: ViewMode;
  changeViewMode: (m: ViewMode) => void;
  navigate: (direction: 1 | -1) => void;
  goToday: () => void;
  onNewAppointment: () => void;
  sidebarOpen: boolean;
  openSidebar: () => void;
  pickerOpen: boolean;
  setPickerOpen: (open: boolean) => void;
  onSelectDate: (d: Date) => void;
  isMobile?: boolean;
  dentistFilter: DentistFilter;
  dentistSettings: Record<string, { color?: string; title?: string }>;
  serviceFilter: ServiceFilter;
  /** Alternância "Por sala" (só na vista de dia, com salas e quem vê a agenda de todos). */
  porSala?: { ligado: boolean; alternar: () => void } | null;
};

const VIEW_OPTIONS = [
  { key: "day", label: "Dia" },
  { key: "week", label: "Semana" },
  { key: "fortnight", label: "Quinzena" },
  { key: "month", label: "Mês" },
] as const;

export function AgendaTopbarActions({
  anchorDate,
  viewMode,
  changeViewMode,
  navigate,
  goToday,
  onNewAppointment,
  sidebarOpen,
  openSidebar,
  pickerOpen,
  setPickerOpen,
  onSelectDate,
  isMobile,
  dentistFilter,
  dentistSettings,
  serviceFilter,
  porSala,
}: Props) {
  const hasClinicServices = useFeature("clinicServices");
  return (
    <div className="flex items-center gap-1.5">
      {/* Calendar picker — agora é o PRIMEIRO botão, com destaque e label "Calendário" */}
      {!sidebarOpen && (
        <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-label="Abrir calendário"
              className="inline-flex h-9 items-center gap-1.5 rounded-md border border-brand-300 bg-gradient-to-br from-brand-50 to-white px-3 text-xs font-semibold text-brand-700 shadow-sm transition-colors hover:border-brand-400 hover:from-brand-100 hover:to-brand-50 dark:border-primary/40 dark:from-primary/15 dark:to-primary/5 dark:text-primary dark:hover:from-primary/25 dark:hover:to-primary/10"
            >
              <Calendar className="h-4 w-4" />
              Calendário
            </button>
          </PopoverTrigger>
          <PopoverContent
            align={isMobile ? "end" : "start"}
            sideOffset={8}
            className="w-auto border-zinc-200 bg-white p-4 shadow-2xl"
          >
            <div className="min-w-[300px] space-y-3">
              <AgendaMiniCalendar
                selectedDate={anchorDate}
                onSelect={(d) => {
                  onSelectDate(d);
                  setPickerOpen(false);
                }}
                size="full"
              />
              <div className="flex gap-2 border-t border-zinc-100 pt-3">
                <button
                  type="button"
                  onClick={() => {
                    goToday();
                    setPickerOpen(false);
                  }}
                  className="inline-flex h-9 flex-1 items-center justify-center gap-1.5 rounded-md bg-brand-600 px-3 text-xs font-medium text-white shadow-sm transition-colors hover:bg-brand-700"
                >
                  <Sun className="h-3.5 w-3.5" />
                  Ir para hoje
                </button>
                {/* "Fixar calendário" só em desktop — sidebar não aparece no mobile */}
                {!isMobile && (
                  <button
                    type="button"
                    onClick={openSidebar}
                    className="inline-flex h-9 flex-1 items-center justify-center gap-1.5 rounded-md border border-brand-200 bg-brand-50 px-3 text-xs font-medium text-brand-700 transition-colors hover:border-brand-300 hover:bg-brand-100"
                  >
                    <Pin className="h-3.5 w-3.5" />
                    Fixar calendário
                  </button>
                )}
              </div>
            </div>
          </PopoverContent>
        </Popover>
      )}

      {/* Dropdown de profissionais — só quando sidebar está oculta (desktop)
          OU sempre no mobile. Na sidebar fixada o filtro aparece inline. */}
      {(!sidebarOpen || isMobile) && !isMobile && (
        <div data-tour="agenda-filtros" className="flex items-center gap-1.5">
          <DentistDropdownButton
            selectedIds={dentistFilter.ids}
            onToggle={dentistFilter.toggle}
            onSelectAll={dentistFilter.selectAll}
            onClear={dentistFilter.clear}
            dentistSettings={dentistSettings}
          />
          {hasClinicServices && (
            <ServiceDropdownButton
              selectedIds={serviceFilter.ids}
              onToggle={serviceFilter.toggle}
              onSelectAll={serviceFilter.selectAll}
              onClear={serviceFilter.clear}
            />
          )}
        </div>
      )}

      <button
        type="button"
        onClick={goToday}
        className="inline-flex h-8 items-center rounded-md border border-zinc-200 bg-white px-2.5 text-[11px] font-medium text-zinc-700 transition-colors hover:border-brand-300 hover:bg-brand-50 hover:text-brand-700 dark:border-border dark:bg-card dark:text-foreground dark:hover:bg-muted"
      >
        Hoje
      </button>

      <div className="inline-flex rounded-md border border-zinc-200 bg-white">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="flex h-8 w-7 items-center justify-center text-zinc-500 hover:bg-zinc-50 hover:text-zinc-900"
          aria-label="Anterior"
        >
          <ChevronLeft className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={() => navigate(1)}
          className="flex h-8 w-7 items-center justify-center border-l border-zinc-200 text-zinc-500 hover:bg-zinc-50 hover:text-zinc-900"
          aria-label="Próximo"
        >
          <ChevronRight className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Toggle de view: desktop só. Mobile força Day sempre. */}
      {!isMobile && (
        <div data-tour="agenda-modo-visualizacao" className="inline-flex h-8 items-center rounded-md border border-zinc-200 bg-white p-0.5">
          {VIEW_OPTIONS.map(({ key, label }) => (
            <button
              key={key}
              type="button"
              onClick={() => changeViewMode(key)}
              className={
                viewMode === key
                  ? "inline-flex h-7 items-center rounded px-2.5 text-[11px] font-semibold bg-brand-600 text-white"
                  : "inline-flex h-7 items-center rounded px-2.5 text-[11px] font-medium text-zinc-600 hover:text-zinc-900"
              }
            >
              {label}
            </button>
          ))}
        </div>
      )}

      {!isMobile && porSala && viewMode === "day" && (
        <button
          type="button"
          aria-pressed={porSala.ligado}
          onClick={porSala.alternar}
          title="Uma coluna por sala"
          className={
            porSala.ligado
              ? "inline-flex h-8 items-center gap-1 rounded-md bg-brand-600 px-2.5 text-[11px] font-semibold text-white"
              : "inline-flex h-8 items-center gap-1 rounded-md border border-zinc-200 bg-white px-2.5 text-[11px] font-medium text-zinc-600 hover:text-zinc-900"
          }
        >
          <DoorOpen className="h-3.5 w-3.5" />
          Por sala
        </button>
      )}

      {/* Botão "Nova Consulta" agora é o ÚLTIMO — só desktop (mobile usa FAB) */}
      {!isMobile && (
        <button
          type="button"
          data-tour="agenda-nova-consulta"
          onClick={onNewAppointment}
          className="inline-flex h-8 items-center gap-1 rounded-md bg-brand-600 px-2.5 text-[11px] font-semibold text-white shadow-sm transition-colors hover:bg-brand-700 sm:px-3"
        >
          <Plus className="h-3.5 w-3.5" />
          Nova Consulta
        </button>
      )}
    </div>
  );
}
